/**
 * THE QUERY FORM OF AN ARRIVAL: `?conference=<id>` and `?event=<id>`
 * (2026-10-11).
 *
 * WHY. A link into Revent named a conference or an event only in its FRAGMENT
 * (`/?app=revent#f=<folder id>`, `#e=<event id>`). That is right for a
 * one-use code, which must never reach a server, and awkward for a link a
 * person writes by hand or an operator puts in an email: the owner asked
 * twice for the plain form, "eg ?app=revent&conference=xxx or
 * ?app=revent&event=yyy". A conference's id and an event's id are not
 * secrets (a public calendar lists both), so they may ride in a query.
 *
 * ONE CODE PATH, NOT A SECOND GRAMMAR. Nothing downstream learns the query
 * form. The two parameters are turned into the EXISTING arrival fragment
 * (lib/auth/arrival-fragment.ts: `e=<event>&f=<folder>`) on the first page
 * that has a window, and from there every reader reads what it always read:
 * the sign-in screen works out the conference's own screen and the event's
 * name from the fragment (lib/auth/login-app.ts), parks it across the
 * sign-in hop, and the app is handed it as `host.workspace.params`
 * (lib/workspaces/fragment-target.ts). So `/?app=revent&conference=la-2026`
 * behaves exactly as `/?app=revent#f=<LA Tech Week's folder>` does, signed
 * in or signed out, on the apex and on an app's own host.
 *
 * THE HOPS, and why the turn is made in the browser. On the apex,
 * `/?app=<id>` is answered by a server redirect (app/(splash)/authed/home):
 * to `/login?...` signed out, to `/workspace?spawn=...` signed in. A server
 * could put the fragment on that redirect itself, but a Location that
 * carries a fragment REPLACES the one the visitor arrived with, and the
 * fragment may be a scanned code. So the server only carries the two
 * parameters along, validated, beside the return address
 * (`withArrivalQuery`, the way campaign tags ride, lib/http/campaign-tags.ts),
 * and the page that renders makes the turn where both forms can be seen
 * (`adoptArrivalQuery`). On an app's own host the typed address, query and
 * all, is the return address (proxy.ts), so the sign-in screen also looks
 * inside `returnTo` for the two parameters.
 *
 * A LINK THAT CARRIES BOTH FORMS: THE FRAGMENT WINS. An arrival fragment on
 * the address (or one parked by the sign-in screen and restored first,
 * components/auth/arrival-fragment-restore.tsx) is kept whole and the query
 * form is dropped: the fragment is the older contract and the only one that
 * can hold a code, and merging the two would let a query steer which
 * conference a scanned code is read against.
 *
 * WHAT IS ACCEPTED, and nothing else. Anything outside these shapes is
 * dropped, never repaired:
 *   conference   a conference's folder id (the lower-case uuid the `f` key
 *                takes), or a published conference's EDITION id
 *                (`<city>-<year>`, e.g. `la-2026`), which is resolved to its
 *                folder with the same function the publisher writes it by
 *                (public/widgets/revent/lib/edition-ids.js); `sf-2026`
 *                resolves to the constant it was published on.
 *   event        a Revent event id, `ev` and twelve hex digits.
 * Neither ever carries a title or any text to draw: an id is only a key, the
 * rule of app-sign-in-and-onboarding.md section 4.5b and
 * SECURITY_ISSUES_LOG.md S-393. An edition id that names no conference
 * derives a folder nobody has, which draws the app's own screen and opens
 * the default week; nothing can be forged onto a screen by naming one.
 *
 * THE ONE-USE CODE STAYS IN THE FRAGMENT. `?c=`, `?r=` and `?n=` are not
 * read here or anywhere: a code in a query would reach the server's logs and
 * every referrer, and a name in a query would be words on the sign-in
 * screen. tests/unit/auth/arrival-query.test.ts pins both.
 *
 * Plain module, client-safe, relative imports only (the bundle file is
 * imported by lib/workspaces/flagship-apps.ts the same way).
 */

import { parseArrivalFragment } from './arrival-fragment';
import { REVENT_SF_2026_PROJECT } from '../workspaces/flagship-apps';
import { conferenceFolderId } from '../../public/widgets/revent/lib/edition-ids.js';

/** The two query parameters, and the only two. */
export const ARRIVAL_QUERY_KEYS = ['conference', 'event'] as const;

const FOLDER_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/* A published conference's edition id; the first arm of Revent's edition grammar. */
const EDITION_ID_RE = /^[a-z]{2,4}-[0-9]{4}$/;
/* A Revent event id (lib/apps/revent-event-names.ts `REVENT_EVENT_ID_RE`; a test holds the two equal). */
const EVENT_ID_RE = /^ev[0-9a-f]{12}$/;
/* The one edition published on hand-written ids, before a conference was data. */
const LEGACY_EDITION_FOLDERS: Readonly<Record<string, string>> = { 'sf-2026': REVENT_SF_2026_PROJECT };
const PROBE_ORIGIN = 'https://arrival-query.invalid';

type SearchInput = string | URLSearchParams | Record<string, string | string[] | undefined> | null | undefined;

function paramOf(search: SearchInput, key: string): string | null {
  if (!search) return null;
  if (typeof search === 'string') {
    try {
      return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(key);
    } catch {
      return null;
    }
  }
  if (search instanceof URLSearchParams) return search.get(key);
  const raw = Object.prototype.hasOwnProperty.call(search, key) ? search[key] : undefined;
  const value = Array.isArray(raw) ? raw[0] : raw;
  return typeof value === 'string' ? value : null;
}

/**
 * The folder a `conference` value names: a folder id as it is, an edition id
 * as the folder it derives (the legacy edition as its constant); null for
 * anything else.
 */
export function conferenceFolderOf(value: unknown): string | null {
  if (typeof value !== 'string' || !value || value.length > 64) return null;
  if (FOLDER_RE.test(value)) return value;
  if (!EDITION_ID_RE.test(value)) return null;
  if (Object.prototype.hasOwnProperty.call(LEGACY_EDITION_FOLDERS, value)) return LEGACY_EDITION_FOLDERS[value];
  return conferenceFolderId(value);
}

/** An `event` value when it is an event id; null for anything else. */
export function eventIdOf(value: unknown): string | null {
  return typeof value === 'string' && EVENT_ID_RE.test(value) ? value : null;
}

/**
 * The arrival fragment (without `#`) a query's `conference` and `event` stand
 * for: `e=<event>&f=<folder>`, either alone, or null when the query names
 * neither in an accepted shape. The answer is in the arrival grammar by
 * construction and is checked against it anyway.
 */
export function arrivalFragmentFromQuery(search: SearchInput): string | null {
  const event = eventIdOf(paramOf(search, 'event'));
  const folder = conferenceFolderOf(paramOf(search, 'conference'));
  const pairs: string[] = [];
  if (event) pairs.push(`e=${event}`);
  if (folder) pairs.push(`f=${folder}`);
  return pairs.length ? parseArrivalFragment(pairs.join('&')) : null;
}

/**
 * The two parameters as a query string to ride a server redirect (no leading
 * `?` or `&`), validated and in their resolved form (`conference` is always
 * the folder id from here on), or `''`.
 */
export function arrivalQuery(search: SearchInput): string {
  const out = new URLSearchParams();
  const folder = conferenceFolderOf(paramOf(search, 'conference'));
  const event = eventIdOf(paramOf(search, 'event'));
  if (folder) out.set('conference', folder);
  if (event) out.set('event', event);
  return out.toString();
}

/** `path` with the request's two parameters appended to its query, beside whatever it already carries. */
export function withArrivalQuery(path: string, search: SearchInput): string {
  const q = arrivalQuery(search);
  if (!q) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${q}`;
}

/** A query string without the two parameters (leading `?` kept when anything is left). */
export function stripArrivalQuery(search: string): string {
  let q: URLSearchParams;
  try {
    q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  } catch {
    return search;
  }
  for (const key of ARRIVAL_QUERY_KEYS) q.delete(key);
  const rest = q.toString();
  return rest ? `?${rest}` : '';
}

/** The query of a same-origin return address (`/?app=revent&conference=...`), or `''`. */
function searchOfReturnTo(returnTo: unknown): string {
  if (typeof returnTo !== 'string' || !returnTo.startsWith('/')) return '';
  try {
    const url = new URL(returnTo, PROBE_ORIGIN);
    return url.origin === PROBE_ORIGIN ? url.search : '';
  } catch {
    return '';
  }
}

/**
 * THE TURN, on a page that has a window: when the address carries the query
 * form and NO fragment, the fragment the query stands for is put on the
 * address and the two parameters are taken off it. True when a fragment was
 * written.
 *
 * `returnTo` is the sign-in screen's return address: on an app's own host the
 * typed query rides inside it, so it is read too (after the page's own
 * query), and left untouched: the destination makes the same turn again once
 * the person is signed in, and both turns answer the same fragment.
 *
 * A fragment already on the address WINS, whatever it is (see the header):
 * nothing is written. When that fragment is an arrival fragment the two
 * parameters are still taken off the address, since they have been outranked
 * and would only be read again by the next page; any other fragment (another
 * app's deep link) leaves the address alone entirely.
 */
export function adoptArrivalQuery(win: Window, opts: { returnTo?: unknown } = {}): boolean {
  let pathname = '';
  let search = '';
  let hash = '';
  try {
    ({ pathname, search, hash } = win.location);
  } catch {
    return false;
  }
  const own = arrivalFragmentFromQuery(search);
  const fragment = own ?? arrivalFragmentFromQuery(searchOfReturnTo(opts.returnTo));
  if (!fragment) return false;
  const rest = own ? stripArrivalQuery(search) : search;
  if (hash && hash !== '#') {
    if (own && parseArrivalFragment(hash)) {
      try {
        win.history.replaceState(null, '', `${pathname}${rest}${hash}`);
      } catch {
        /* the address stays as it was */
      }
    }
    return false;
  }
  try {
    win.history.replaceState(null, '', `${pathname}${rest}#${fragment}`);
  } catch {
    return false;
  }
  return true;
}
